import * as THREE from "three";
import { WallType } from "../types/floorplannerTypes";
import type { JointInfo } from "./JointDetector";
import { calculateWallEdgePoint, getWallDirectionAt } from "./edgeConnectionHelpers";

type DefaultWallWidth = () => number;

/**
 * Multi-joint endpoint trimming - angular-adjacency miter corners.
 *
 * A multi-joint (3+ walls meeting at a point) is resolved by trimming each wall's
 * fill/outline end so neighbouring walls meet at a shared mitre corner, leaving a
 * clean central gap that the joint fill polygon fills exactly.
 *
 * The corner between two walls must be computed ONCE from BOTH walls' facing edges,
 * otherwise each wall picks its own (possibly different) point and the geometry no
 * longer tiles - producing fill gaps and stray/!missing outline near the joint.
 *
 * Model: each wall contributes an "arm" - a ray leaving the joint along the wall,
 * with a left and right edge line (in the arm's own away-frame). A through-wall
 * (middle connection) contributes TWO arms (one per direction). Sort the arms by
 * their away-angle; for every pair of angularly-adjacent arms A (CW) and B (CCW)
 * the empty wedge between them is closed by the single corner
 *     corner = A.leftEdge  ∩  B.rightEdge
 * which is assigned to BOTH A's left side and B's right side. Adjacent walls
 * therefore share the exact same corner by construction.
 */

interface InfiniteLine {
  point: THREE.Vector2;
  dir: THREE.Vector2;
}

interface JointArm {
  wallId: string;
  endpoint: "start" | "end" | "middle";
  width: number;
  centerPoint: THREE.Vector2;
  awayDir: THREE.Vector2;
  awayAngle: number;
  /** Edge lines in the arm's away-frame (left = CCW side, right = CW side). */
  leftLine: InfiniteLine;
  rightLine: InfiniteLine;
  /** True when the arm's away-frame "left" coincides with the wall's "left" side. */
  armLeftIsWallLeft: boolean;
  /** Filled during the adjacency pass (away-frame). */
  leftCorner: THREE.Vector2 | null;
  rightCorner: THREE.Vector2 | null;
}

const intersectInfiniteLines = (a: InfiniteLine, b: InfiniteLine): THREE.Vector2 | null => {
  const cross = a.dir.x * b.dir.y - a.dir.y * b.dir.x;
  if (Math.abs(cross) < 1e-10) {
    return null;
  }
  const delta = b.point.clone().sub(a.point);
  const t = (delta.x * b.dir.y - delta.y * b.dir.x) / cross;
  return a.point.clone().addScaledVector(a.dir, t);
};

const hasPositiveLateralOverlap = (a: JointArm, b: JointArm): boolean => {
  const normal = new THREE.Vector2(-a.awayDir.y, a.awayDir.x);
  const lateralDistance = Math.abs(b.centerPoint.clone().sub(a.centerPoint).dot(normal));
  return (a.width + b.width) / 2 - lateralDistance > 1e-6;
};

/**
 * Build the arms for every wall participating in the joint. Straight/arc walls
 * connected at an endpoint contribute one arm; through-walls (middle) two.
 */
const buildJointArms = (joint: JointInfo, defaultWallWidth: DefaultWallWidth): JointArm[] => {
  const arms: JointArm[] = [];

  for (const connection of joint.connectedWalls) {
    const wall = connection.wall;
    if (!wall) {
      continue;
    }

    const position = connection.connectionPosition;
    const projectedPoint = position === "middle" ? joint.point : undefined;
    const centerPoint = position === "start"
      ? wall.start.clone()
      : position === "end"
        ? wall.end.clone()
        : (projectedPoint ?? wall.start.clone().add(wall.end).multiplyScalar(0.5)).clone();

    // Forward tangent/chord at the connection point, and the wall's own left/right
    // edge lines (wall convention). Both share the forward direction.
    const forward = getWallDirectionAt(wall, position, projectedPoint);
    if (forward.lengthSq() < 1e-12) {
      continue;
    }
    forward.normalize();

    const leftEdge = calculateWallEdgePoint(wall, position, 0, wall.wallWidth, projectedPoint, "left").edgePosition;
    const rightEdge = calculateWallEdgePoint(wall, position, 0, wall.wallWidth, projectedPoint, "right").edgePosition;
    const wallLeftLine: InfiniteLine = { point: leftEdge, dir: forward.clone() };
    const wallRightLine: InfiniteLine = { point: rightEdge, dir: forward.clone() };

    // "away" directions leaving the joint along this wall.
    const awayDirs: THREE.Vector2[] = position === "middle"
      ? [forward.clone(), forward.clone().negate()]
      : [position === "end" ? forward.clone().negate() : forward.clone()];

    for (const awayDir of awayDirs) {
      // arm.left (CCW of awayDir) coincides with the wall's left side only when the
      // away direction matches the forward (chord) direction.
      const armLeftIsWallLeft = awayDir.dot(forward) > 0;
      arms.push({
        wallId: connection.wallId,
        endpoint: position,
        width: wall.wallWidth ?? defaultWallWidth(),
        centerPoint,
        awayDir,
        awayAngle: Math.atan2(awayDir.y, awayDir.x),
        leftLine: armLeftIsWallLeft ? wallLeftLine : wallRightLine,
        rightLine: armLeftIsWallLeft ? wallRightLine : wallLeftLine,
        armLeftIsWallLeft,
        leftCorner: null,
        rightCorner: null,
      });
    }
  }

  return arms;
};

/**
 * Resolve every adjacent-arm miter corner and store it on both participating arms.
 * The corner is rejected (left null) when the wedge is reflex/near-collinear or the
 * miter would run far outside the joint neighbourhood - in those cases the wall is
 * left untrimmed and the joint fill polygon closes the gap instead.
 */
const resolveArmCorners = (arms: JointArm[], jointPoint: THREE.Vector2, clampRadius: number): void => {
  const sorted = arms.slice().sort((a, b) => a.awayAngle - b.awayAngle);
  const n = sorted.length;
  if (n < 2) {
    return;
  }

  for (let i = 0; i < n; i++) {
    const armCW = sorted[i];
    const armCCW = sorted[(i + 1) % n];

    let corner = intersectInfiniteLines(armCW.leftLine, armCCW.rightLine);
    if (corner && !Number.isFinite(corner.x + corner.y)) {
      corner = null;
    }
    if (corner) {
      // The corner must sit inside the joint neighbourhood and be forward (outward)
      // along BOTH arms - a convex mitre. Reflex/near-collinear wedges intersect
      // "behind" the joint; those sides are left untrimmed and the joint fill closes
      // the gap. Accepting/rejecting the SHARED corner together (rather than per arm)
      // keeps both walls' geometry consistent and avoids opening fill holes at
      // obtuse joints where the coverage-based fill cannot reach a one-sided trim.
      const fromJoint = corner.clone().sub(jointPoint);
      const outward = fromJoint.dot(armCW.awayDir) > -1e-4 && fromJoint.dot(armCCW.awayDir) > -1e-4;
      if (!outward || corner.distanceTo(jointPoint) > clampRadius) {
        corner = null;
      }
    }

    armCW.leftCorner = corner;
    armCCW.rightCorner = corner;
  }

  for (let i = 0; i < n; i++) {
    const arm = sorted[i];
    if (arm.endpoint === "middle" || !arm.width || (arm.leftCorner && arm.rightCorner) || (!arm.leftCorner && !arm.rightCorner)) {
      continue;
    }

    const leftNeighbor = sorted[(i + 1) % n];
    const rightNeighbor = sorted[(i - 1 + n) % n];
    const sideStep = new THREE.Vector2(-arm.awayDir.y, arm.awayDir.x).multiplyScalar(arm.width);

    // If this endpoint overlaps a collinear/opposite participating wall, one side
    // has no angular miter because that side is hidden by the opposite wall. Carry
    // the valid trim station across the wall width so fill and outline stop at the
    // multi-joint interface instead of falling back to the raw endpoint corner.
    if (
      !arm.leftCorner &&
      arm.rightCorner &&
      arm.awayDir.dot(leftNeighbor.awayDir) < -1 + 1e-6 &&
      hasPositiveLateralOverlap(arm, leftNeighbor)
    ) {
      const completed = arm.rightCorner.clone().add(sideStep);
      if (completed.distanceTo(jointPoint) <= clampRadius) {
        arm.leftCorner = completed;
      }
    } else if (
      !arm.rightCorner &&
      arm.leftCorner &&
      arm.awayDir.dot(rightNeighbor.awayDir) < -1 + 1e-6 &&
      hasPositiveLateralOverlap(arm, rightNeighbor)
    ) {
      const completed = arm.leftCorner.clone().sub(sideStep);
      if (completed.distanceTo(jointPoint) <= clampRadius) {
        arm.rightCorner = completed;
      }
    }
  }
};

const getSourceArmCorners = (
  joint: JointInfo,
  wall: WallType,
  endpoint: "start" | "end",
  defaultWallWidth: DefaultWallWidth,
): { wallLeft: THREE.Vector2 | null; wallRight: THREE.Vector2 | null } | null => {
  if (joint.connectedWalls.length < 3) {
    return null;
  }

  const widths = joint.connectedWalls.map(connection => connection.wall?.wallWidth ?? defaultWallWidth());
  const maxWidth = Math.max(...widths, defaultWallWidth());
  const clampRadius = Math.max(maxWidth * 4, 0.001);

  const arms = buildJointArms(joint, defaultWallWidth);
  resolveArmCorners(arms, joint.point, clampRadius);

  const arm = arms.find(candidate => candidate.wallId === wall.id && candidate.endpoint === endpoint);
  if (!arm) {
    return null;
  }

  // Map the arm's away-frame corners back to the wall's left/right sides.
  const wallLeft = arm.armLeftIsWallLeft ? arm.leftCorner : arm.rightCorner;
  const wallRight = arm.armLeftIsWallLeft ? arm.rightCorner : arm.leftCorner;
  return { wallLeft, wallRight };
};

/** Raw (untrimmed) edge corner of a wall at an endpoint/side. */
const rawEdgeCorner = (
  wall: WallType,
  endpoint: "start" | "end",
  side: "left" | "right",
): THREE.Vector2 =>
  calculateWallEdgePoint(wall, endpoint, 0, wall.wallWidth, undefined, side).edgePosition;

export const getMultiJointEndpointTrimPointForJoint = (
  joint: JointInfo,
  wall: WallType,
  endpoint: "start" | "end",
  side: "left" | "right",
  _lineStart: THREE.Vector2,
  _lineEnd: THREE.Vector2,
  defaultWallWidth: DefaultWallWidth,
): THREE.Vector2 | null => {
  if (joint.connectedWalls.length < 3 || wall.lineForm === "arc") {
    return null;
  }
  const corners = getSourceArmCorners(joint, wall, endpoint, defaultWallWidth);
  if (!corners) {
    return null;
  }
  // Sides are independent: use this side's mitre if it exists, otherwise leave it
  // at the raw edge (no trim). Returning the raw point - rather than null - keeps
  // the outline from falling back to a different (pairwise-intersection) trim, so
  // fill and outline always agree on this side even when the OTHER side mitres.
  const corner = side === "left" ? corners.wallLeft : corners.wallRight;
  return (corner ?? rawEdgeCorner(wall, endpoint, side)).clone();
};

export const getMultiJointEndpointTrimPairForJoint = (
  joint: JointInfo,
  wall: WallType,
  endpoint: "start" | "end",
  defaultWallWidth: DefaultWallWidth,
): { left: THREE.Vector2; right: THREE.Vector2 } | null => {
  if (joint.connectedWalls.length < 3 || wall.lineForm === "arc") {
    return null;
  }

  const corners = getSourceArmCorners(joint, wall, endpoint, defaultWallWidth);
  if (!corners) {
    return null;
  }

  // No mitre on either side -> nothing to trim (the joint fill closes the gap).
  if (!corners.wallLeft && !corners.wallRight) {
    return null;
  }

  // Trim each side independently; an un-mitred side stays at its raw edge so the
  // mitred side is still applied (previously both sides were dropped together,
  // leaving a gap in the wall outline/fill at the joint).
  return {
    left: (corners.wallLeft ?? rawEdgeCorner(wall, endpoint, "left")).clone(),
    right: (corners.wallRight ?? rawEdgeCorner(wall, endpoint, "right")).clone(),
  };
};
